/**
 * ReHub credit policy v3 — flexible monthly plans, annual prepay, and
 * expiring top-ups.
 *
 * GBP, EUR and USD amounts below are fixed retail prices.
 * Do not convert one currency into another at display or checkout time.
 *
 * v1 and v2 catalogues stay authoritative for already-sold Price IDs.
 * This module does not change what those prices grant.
 *
 * New checkout is on unless REHUB_PRICING_V3_SALES or VITE_REHUB_PRICING_V3_SALES
 * is set to false. Renewal fulfilment uses configured Price IDs even when new
 * sales are off, so a rollback stops new checkouts without dropping paid grants.
 */

import {
  PHOTO_CLEAR_CREDIT_COST,
  STAGING_STANDARD_CREDIT_COST,
  IMAGEMOTION_STANDARD_CREDIT_COST,
} from './credit-actions';
import { getPackStripePriceIdV2, REHUB_CREDIT_PACKS_V2, RETIRED_TOPUP_PRICE_IDS } from './credit-packs-v2';

export const REHUB_CREDIT_POLICY_V3 = 'v3' as const;
export const REHUB_CREDIT_POLICY_V3_ID = 'rehub-credits-2026-09' as const;

export const V3_TOPUP_EXPIRY_MONTHS = 12;
export const V3_MONTHLY_EXPIRY_MONTHS = 6;
export const V3_ANNUAL_EXPIRY_MONTHS = 12;

export const V3_SALES_ENV_KEYS = ['REHUB_PRICING_V3_SALES', 'VITE_REHUB_PRICING_V3_SALES'] as const;

export type CreditPolicyVersion = 'v1' | 'v2' | 'v3';
export type V3BillingInterval = 'month' | 'year';
export type V3DisplayId = 'starter' | 'pro' | 'studio';
export type V3Currency = 'GBP' | 'EUR' | 'USD';

export interface V3Money {
  GBP: number;
  EUR: number;
  USD: number;
}

export const V3_CURRENCIES: readonly V3Currency[] = ['GBP', 'EUR', 'USD'];

export type V3PlanId =
  | 'starter_v4'
  | 'pro_v4'
  | 'studio_v4'
  | 'starter_v4_annual'
  | 'pro_v4_annual'
  | 'studio_v4_annual';

export interface V3PlanDefinition {
  id: V3PlanId;
  policyVersion: 'v3';
  policyId: typeof REHUB_CREDIT_POLICY_V3_ID;
  displayId: V3DisplayId;
  label: string;
  interval: V3BillingInterval;
  /** Credits granted once per successful payment. Annual is the full year, not a monthly drip. */
  credits: number;
  /** Integer minor units for each supported currency. */
  amountMinor: V3Money;
  /** Integer pence. Same as amountMinor.GBP. */
  amountMinorGbp: number;
  seats: number;
  expiryMonths: number;
  /** No rollover cap. Expiry is the only limit on unused subscription credits. */
  rolloverCap: null;
  badge?: 'popular';
  sortOrder: number;
}

export interface V3TopUpDefinition {
  id: 'credits_20' | 'credits_50_v2' | 'credits_150_v2' | 'credits_500';
  policyVersion: 'v3';
  label: string;
  credits: number;
  amountMinor: V3Money;
  amountMinorGbp: number;
  expiryMonths: typeof V3_TOPUP_EXPIRY_MONTHS;
  neverExpires: false;
  badge?: 'popular' | 'high-volume';
  sortOrder: number;
}

const PLAN = (
  partial: Omit<V3PlanDefinition, 'policyVersion' | 'policyId' | 'rolloverCap' | 'amountMinorGbp'>
): V3PlanDefinition => ({
  ...partial,
  amountMinorGbp: partial.amountMinor.GBP,
  policyVersion: 'v3',
  policyId: REHUB_CREDIT_POLICY_V3_ID,
  rolloverCap: null,
});

export const REHUB_CREDIT_MONTHLY_PLANS_V3: readonly V3PlanDefinition[] = [
  PLAN({
    id: 'starter_v4',
    displayId: 'starter',
    label: 'Starter',
    interval: 'month',
    credits: 40,
    amountMinor: { GBP: 2400, EUR: 2900, USD: 3400 },
    seats: 1,
    expiryMonths: V3_MONTHLY_EXPIRY_MONTHS,
    sortOrder: 10,
  }),
  PLAN({
    id: 'pro_v4',
    displayId: 'pro',
    label: 'Pro',
    interval: 'month',
    credits: 130,
    amountMinor: { GBP: 6900, EUR: 7900, USD: 9500 },
    seats: 3,
    expiryMonths: V3_MONTHLY_EXPIRY_MONTHS,
    badge: 'popular',
    sortOrder: 20,
  }),
  PLAN({
    id: 'studio_v4',
    displayId: 'studio',
    label: 'Studio',
    interval: 'month',
    credits: 350,
    amountMinor: { GBP: 16900, EUR: 19900, USD: 22900 },
    seats: 10,
    expiryMonths: V3_MONTHLY_EXPIRY_MONTHS,
    sortOrder: 30,
  }),
] as const;

export const REHUB_CREDIT_ANNUAL_PLANS_V3: readonly V3PlanDefinition[] = [
  PLAN({
    id: 'starter_v4_annual',
    displayId: 'starter',
    label: 'Starter',
    interval: 'year',
    credits: 480,
    amountMinor: { GBP: 22800, EUR: 27600, USD: 31200 },
    seats: 1,
    expiryMonths: V3_ANNUAL_EXPIRY_MONTHS,
    sortOrder: 10,
  }),
  PLAN({
    id: 'pro_v4_annual',
    displayId: 'pro',
    label: 'Pro',
    interval: 'year',
    credits: 1560,
    amountMinor: { GBP: 70800, EUR: 82800, USD: 94800 },
    seats: 3,
    expiryMonths: V3_ANNUAL_EXPIRY_MONTHS,
    badge: 'popular',
    sortOrder: 20,
  }),
  PLAN({
    id: 'studio_v4_annual',
    displayId: 'studio',
    label: 'Studio',
    interval: 'year',
    credits: 4200,
    amountMinor: { GBP: 178800, EUR: 214800, USD: 238800 },
    seats: 10,
    expiryMonths: V3_ANNUAL_EXPIRY_MONTHS,
    sortOrder: 30,
  }),
] as const;

export const REHUB_CREDIT_PLANS_V3: readonly V3PlanDefinition[] = [
  ...REHUB_CREDIT_MONTHLY_PLANS_V3,
  ...REHUB_CREDIT_ANNUAL_PLANS_V3,
];

const TOPUP = (
  partial: Omit<V3TopUpDefinition, 'policyVersion' | 'amountMinorGbp' | 'expiryMonths' | 'neverExpires'>
): V3TopUpDefinition => ({
  ...partial,
  amountMinorGbp: partial.amountMinor.GBP,
  policyVersion: 'v3',
  expiryMonths: V3_TOPUP_EXPIRY_MONTHS,
  neverExpires: false,
});

export const REHUB_CREDIT_TOPUPS_V3: readonly V3TopUpDefinition[] = [
  TOPUP({ id: 'credits_20', label: 'Small', credits: 20, amountMinor: { GBP: 1500, EUR: 1800, USD: 1900 }, sortOrder: 10 }),
  TOPUP({ id: 'credits_50_v2', label: 'Popular', credits: 50, amountMinor: { GBP: 3500, EUR: 4200, USD: 4400 }, badge: 'popular', sortOrder: 20 }),
  TOPUP({ id: 'credits_150_v2', label: 'Large', credits: 150, amountMinor: { GBP: 9000, EUR: 10800, USD: 11300 }, badge: 'high-volume', sortOrder: 30 }),
  TOPUP({ id: 'credits_500', label: 'Bulk', credits: 500, amountMinor: { GBP: 25000, EUR: 30000, USD: 31300 }, badge: 'high-volume', sortOrder: 40 }),
] as const;

const V3_PLAN_ENV_BASE: Record<V3PlanId, string> = {
  starter_v4: 'STRIPE_PRICE_SUB_STARTER_V4',
  pro_v4: 'STRIPE_PRICE_SUB_PRO_V4',
  studio_v4: 'STRIPE_PRICE_SUB_STUDIO_V4',
  starter_v4_annual: 'STRIPE_PRICE_SUB_STARTER_V4_ANNUAL',
  pro_v4_annual: 'STRIPE_PRICE_SUB_PRO_V4_ANNUAL',
  studio_v4_annual: 'STRIPE_PRICE_SUB_STUDIO_V4_ANNUAL',
};

/** GBP keys kept for existing env files. EUR and USD use the same base plus the currency. */
export const V3_PLAN_ENV_KEYS: Record<V3PlanId, { vite: string; server: string }> = {
  starter_v4: { vite: 'VITE_STRIPE_PRICE_SUB_STARTER_V4_GBP', server: 'STRIPE_PRICE_SUB_STARTER_V4_GBP' },
  pro_v4: { vite: 'VITE_STRIPE_PRICE_SUB_PRO_V4_GBP', server: 'STRIPE_PRICE_SUB_PRO_V4_GBP' },
  studio_v4: { vite: 'VITE_STRIPE_PRICE_SUB_STUDIO_V4_GBP', server: 'STRIPE_PRICE_SUB_STUDIO_V4_GBP' },
  starter_v4_annual: { vite: 'VITE_STRIPE_PRICE_SUB_STARTER_V4_ANNUAL_GBP', server: 'STRIPE_PRICE_SUB_STARTER_V4_ANNUAL_GBP' },
  pro_v4_annual: { vite: 'VITE_STRIPE_PRICE_SUB_PRO_V4_ANNUAL_GBP', server: 'STRIPE_PRICE_SUB_PRO_V4_ANNUAL_GBP' },
  studio_v4_annual: { vite: 'VITE_STRIPE_PRICE_SUB_STUDIO_V4_ANNUAL_GBP', server: 'STRIPE_PRICE_SUB_STUDIO_V4_ANNUAL_GBP' },
};

export function v3PlanPriceEnvKeys(planId: V3PlanId, currency: V3Currency): { vite: string; server: string } {
  const server = `${V3_PLAN_ENV_BASE[planId]}_${currency}`;
  return { server, vite: `VITE_${server}` };
}

const V3_TOPUP_QTY: Record<V3TopUpDefinition['id'], string> = {
  credits_20: '20',
  credits_50_v2: '50',
  credits_150_v2: '150',
  credits_500: '500',
};

export function v3TopUpPriceEnvKeys(packId: V3TopUpDefinition['id'], currency: V3Currency): { vite: string; server: string } {
  const server = `STRIPE_PRICE_TOPUP_${V3_TOPUP_QTY[packId]}_V3_${currency}`;
  return { server, vite: `VITE_${server}` };
}

export function normalizeV3Currency(raw: string | null | undefined): V3Currency | null {
  const value = String(raw || '').toUpperCase();
  if (value === 'GBP' || value === 'EUR' || value === 'USD') return value;
  return null;
}

const PLAN_ALIASES: Record<string, V3PlanId> = {
  starter_v4: 'starter_v4',
  pro_v4: 'pro_v4',
  studio_v4: 'studio_v4',
  starter_v4_annual: 'starter_v4_annual',
  pro_v4_annual: 'pro_v4_annual',
  studio_v4_annual: 'studio_v4_annual',
};

export type StripePriceEnv = Partial<Record<string, string | undefined>>;

export function isCreditPolicyV3SalesEnabled(env?: StripePriceEnv): boolean {
  const source = env ?? readDefaultSalesEnv();
  for (const key of V3_SALES_ENV_KEYS) {
    const value = source[key];
    if (value === 'false' || value === '0') return false;
    if (value === 'true' || value === '1') return true;
  }
  return true;
}

function readDefaultSalesEnv(): StripePriceEnv {
  const out: StripePriceEnv = {};
  if (typeof process !== 'undefined' && process.env) {
    for (const key of V3_SALES_ENV_KEYS) out[key] = process.env[key];
  }
  try {
    // Cast through unknown so Next.js (no Vite ImportMeta.env) and Vite both typecheck.
    const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    for (const key of V3_SALES_ENV_KEYS) {
      const value = viteEnv?.[key];
      if (value) out[key] = value;
    }
  } catch {
    // Node test runner has no Vite env object.
  }
  return out;
}

export function getV3Plan(id: string | null | undefined): V3PlanDefinition | null {
  if (!id) return null;
  const normalized = PLAN_ALIASES[id];
  if (!normalized) return null;
  return REHUB_CREDIT_PLANS_V3.find((plan) => plan.id === normalized) ?? null;
}

export function isV3PlanId(id: string | null | undefined): id is V3PlanId {
  return !!id && id in PLAN_ALIASES;
}

export function getV3MonthlyPlan(displayId: V3DisplayId): V3PlanDefinition {
  const plan = REHUB_CREDIT_MONTHLY_PLANS_V3.find((row) => row.displayId === displayId);
  if (!plan) throw new Error(`Missing v3 monthly plan ${displayId}`);
  return plan;
}

export function getV3AnnualPlan(displayId: V3DisplayId): V3PlanDefinition {
  const plan = REHUB_CREDIT_ANNUAL_PLANS_V3.find((row) => row.displayId === displayId);
  if (!plan) throw new Error(`Missing v3 annual plan ${displayId}`);
  return plan;
}

/** Cash saved versus twelve payments of the current monthly price, in the same currency. */
export function annualCashSavingMinor(displayId: V3DisplayId, currency: V3Currency = 'GBP'): number {
  const monthly = getV3MonthlyPlan(displayId);
  const annual = getV3AnnualPlan(displayId);
  return monthly.amountMinor[currency] * 12 - annual.amountMinor[currency];
}

/** Explanatory monthly equivalent of an annual prepay. Not an instalment. */
export function annualMonthlyEquivalentMinor(displayId: V3DisplayId, currency: V3Currency = 'GBP'): number {
  return Math.round(getV3AnnualPlan(displayId).amountMinor[currency] / 12);
}

/**
 * Display-only pence per credit, rounded half-up to one decimal.
 * Never use this string to compute a charge.
 */
export function formatGbpPencePerCredit(amountMinor: number, credits: number): string {
  if (!credits) return '0.0p';
  const pence = amountMinor / credits;
  return `${pence.toFixed(1)}p`;
}

export function formatGbpFromMinor(amountMinor: number): string {
  return formatV3Money(amountMinor, 'GBP');
}

export function formatV3Money(amountMinor: number, currency: V3Currency): string {
  return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100);
}

/** Display-only unit rate. GBP uses pence. EUR and USD use cents. Never use this to charge. */
export function formatV3UnitRate(amountMinor: number, credits: number, currency: V3Currency): string {
  if (!credits) return currency === 'GBP' ? '0.0p' : '0.0c';
  const unit = (amountMinor / credits).toFixed(1);
  if (currency === 'GBP') return `${unit}p`;
  if (currency === 'USD') return `${unit}¢`;
  return `${unit}c`;
}

export interface V3PlanQuote {
  plan: V3PlanDefinition;
  priceLabel: string;
  billedLabel: string;
  equivalentLabel: string | null;
  creditsLabel: string;
  perCreditLabel: string;
  savingLabel: string | null;
  savingMinor: number | null;
  validityLabel: string;
  seats: number;
}

export function monthlySavingVsPacksMinor(credits: number, planAmountMinor: number, currency: V3Currency = 'GBP'): number {
  const pack = REHUB_CREDIT_TOPUPS_V3[0];
  const packCost = Math.round((credits * pack.amountMinor[currency]) / pack.credits);
  return Math.max(0, packCost - planAmountMinor);
}

export function quoteV3Plan(plan: V3PlanDefinition, currency: V3Currency = 'GBP'): V3PlanQuote {
  const amount = plan.amountMinor[currency];
  const priceLabel = formatV3Money(amount, currency);
  const perCreditLabel = formatV3UnitRate(amount, plan.credits, currency);
  if (plan.interval === 'year') {
    const savingMinor = annualCashSavingMinor(plan.displayId, currency);
    const equivalent = annualMonthlyEquivalentMinor(plan.displayId, currency);
    return {
      plan,
      priceLabel,
      billedLabel: `${priceLabel} billed annually`,
      equivalentLabel: `Equivalent to ${formatV3Money(equivalent, currency)}/month`,
      creditsLabel: `${plan.credits.toLocaleString('en-GB')} credits available immediately`,
      perCreditLabel,
      savingLabel: `Save ${formatV3Money(savingMinor, currency)}`,
      savingMinor,
      validityLabel: CREDIT_POLICY_COPY_V3.annual,
      seats: plan.seats,
    };
  }
  const savingMinor = monthlySavingVsPacksMinor(plan.credits, amount, currency);
  return {
    plan,
    priceLabel,
    billedLabel: `${priceLabel} billed monthly`,
    equivalentLabel: null,
    creditsLabel: `${plan.credits.toLocaleString('en-GB')} credits each month`,
    perCreditLabel,
    savingLabel: `Save ${formatV3Money(savingMinor, currency)} vs credit packs`,
    savingMinor,
    validityLabel: CREDIT_POLICY_COPY_V3.monthly,
    seats: plan.seats,
  };
}

export const CREDIT_POLICY_COPY_V3 = {
  topUp: 'Pay once. Credits are valid for 12 months from purchase.',
  monthly: "Billed monthly. Cancel renewal anytime. Each month's credits are valid for 6 months.",
  annual: 'Pay annually. Get all your credits upfront, valid for 12 months. Turn off renewal anytime.',
  cancellation: 'Your plan will end on [date]. Your remaining paid credits keep their existing expiry dates.',
  legacyBalance: 'Credits already purchased on a non-expiring offer stay non-expiring.',
  managePlan: 'You already have a credit plan. Manage it instead of starting a second subscription. Top-ups stay available.',
  currencyUnavailable: 'Checkout is temporarily unavailable in this currency.',
  examplesAreAlternatives: 'Examples are alternative uses of the same credit pool, not a stack of every example.',
  scheduledChange: 'This change starts on the next renewal. No extra credits are added until that payment succeeds.',
} as const;

export function cancellationCopy(periodEndLabel: string): string {
  return `Your plan will end on ${periodEndLabel}. Your remaining paid credits keep their existing expiry dates.`;
}

/** Alternative uses of one allowance. Not cumulative. */
export function alternativeUsageExamples(credits: number): { count: number; label: string }[] {
  const photo = Math.max(1, PHOTO_CLEAR_CREDIT_COST);
  const staged = Math.max(1, STAGING_STANDARD_CREDIT_COST);
  const motion = Math.max(1, IMAGEMOTION_STANDARD_CREDIT_COST);
  const phrase = (count: number, singular: string, plural: string) => ({
    count,
    label: count === 1 ? singular : plural,
  });
  return [
    phrase(Math.floor(credits / photo), 'PhotoClear declutter', 'PhotoClear declutters'),
    phrase(Math.floor(credits / staged), 'standard staged image', 'standard staged images'),
    phrase(Math.floor(credits / motion), 'standard ImageMotion animation', 'standard ImageMotion animations'),
  ];
}

/**
 * Add calendar months in UTC. Day-of-month clamps to the last valid day
 * (31 Jan + 1 month in a non-leap year is 28 Feb; 29 Feb + 12 months is 28 Feb).
 */
export function addCalendarMonths(from: Date, months: number): Date {
  if (!Number.isInteger(months)) throw new Error('expiry months must be an integer');
  const year = from.getUTCFullYear();
  const month = from.getUTCMonth();
  const day = from.getUTCDate();
  const hours = from.getUTCHours();
  const minutes = from.getUTCMinutes();
  const seconds = from.getUTCSeconds();
  const ms = from.getUTCMilliseconds();
  const targetMonthIndex = month + months;
  const lastDay = new Date(Date.UTC(year, targetMonthIndex + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, targetMonthIndex, Math.min(day, lastDay), hours, minutes, seconds, ms));
}

/** Credits are spendable only while now is strictly before expires_at. */
export function creditsSpendableAt(expiresAt: Date | null, now: Date): boolean {
  if (!expiresAt) return true;
  return now.getTime() < expiresAt.getTime();
}

export function expiryMonthsForV3Grant(source: 'topup' | 'month' | 'year'): number {
  if (source === 'month') return V3_MONTHLY_EXPIRY_MONTHS;
  if (source === 'year') return V3_ANNUAL_EXPIRY_MONTHS;
  return V3_TOPUP_EXPIRY_MONTHS;
}

/**
 * Scheduled plan changes grant nothing. The paid renewal grants the new allowance once.
 */
export function creditsForScheduledPlanChange(phase: 'scheduled' | 'paid_renewal', allowance: number): number {
  if (phase === 'scheduled') return 0;
  if (!Number.isInteger(allowance) || allowance < 0) return 0;
  return allowance;
}

const BLOCKING_SUBSCRIPTION_STATUSES = new Set([
  'active',
  'trialing',
  'past_due',
  'incomplete',
  'paused',
  'unpaid',
]);

/**
 * A scheduled cancellation is still an existing subscription.
 * Canceled and incomplete_expired subscriptions do not block a new checkout.
 */
export function creditSubscriptionBlocksNewCheckout(
  status: string | null | undefined,
  cancelAtPeriodEnd = false
): boolean {
  if (!status) return false;
  if (status === 'canceled' || status === 'incomplete_expired') return false;
  if (cancelAtPeriodEnd && BLOCKING_SUBSCRIPTION_STATUSES.has(status)) return true;
  return BLOCKING_SUBSCRIPTION_STATUSES.has(status);
}

/**
 * Live Stripe Price IDs for the v3 catalogue (acct_1SoLG0DHlg75FZHH).
 * An environment variable still wins, so a project can override one price.
 * Existing v2 Price IDs stay in place for current subscribers.
 */
const LIVE_V3_PLAN_PRICE_IDS: Record<V3PlanId, Record<V3Currency, string>> = {
  starter_v4: {
    GBP: 'price_1UIp6PDHlg75FZHHZv5tCteG',
    EUR: 'price_1UIp6QDHlg75FZHHwTuZcJGF',
    USD: 'price_1UIp6RDHlg75FZHHCXE5SDMl',
  },
  pro_v4: {
    GBP: 'price_1UIp6SDHlg75FZHHeDkUy496',
    EUR: 'price_1UIp6UDHlg75FZHHW1Fg3WLu',
    USD: 'price_1UIp6VDHlg75FZHHYFNjb4nl',
  },
  studio_v4: {
    GBP: 'price_1UIp6WDHlg75FZHHUdS8O74h',
    EUR: 'price_1UIp6XDHlg75FZHHF2cbbTck',
    USD: 'price_1UIp6YDHlg75FZHH3Li9hEWH',
  },
  starter_v4_annual: {
    GBP: 'price_1UIp6ZDHlg75FZHHkfeCPXwg',
    EUR: 'price_1UIp6aDHlg75FZHHsvCwI6Vs',
    USD: 'price_1UIp6bDHlg75FZHHIvY1pcFQ',
  },
  pro_v4_annual: {
    GBP: 'price_1UIp6bDHlg75FZHHPMm4SYYN',
    EUR: 'price_1UIp6cDHlg75FZHH7D2uOzjR',
    USD: 'price_1UIp6dDHlg75FZHHOU2Nex0M',
  },
  studio_v4_annual: {
    GBP: 'price_1UIp6eDHlg75FZHHcGsMrFtx',
    EUR: 'price_1UIp6fDHlg75FZHHfVIHs0a4',
    USD: 'price_1UIp6gDHlg75FZHHScY0IYt2',
  },
};

/** Each ID must charge exactly the matching REHUB_CREDIT_TOPUPS_V3 amount. */
const LIVE_V3_TOPUP_PRICE_IDS: Record<V3TopUpDefinition['id'], Partial<Record<V3Currency, string>>> = {
  credits_20: { EUR: 'price_1UKau1DHlg75FZHHt7BlsBkR', USD: 'price_1UKazEDHlg75FZHH0Bu2HeFV' },
  credits_50_v2: { EUR: 'price_1UESl7DHlg75FZHHCDgRVR8W', USD: 'price_1UKazEDHlg75FZHHXYOARu6a' },
  credits_150_v2: { EUR: 'price_1UKau4DHlg75FZHHibS538N0', USD: 'price_1UKazEDHlg75FZHHKUkJtS2J' },
  credits_500: { EUR: 'price_1UKau6DHlg75FZHHJdeKQVdA', USD: 'price_1UKazEDHlg75FZHHK1WGTceh' },
};

export function getV3PlanStripePriceId(
  plan: V3PlanDefinition,
  env: StripePriceEnv = {},
  currency: V3Currency = 'GBP'
): string {
  const keys = v3PlanPriceEnvKeys(plan.id, currency);
  return env[keys.vite] || env[keys.server] || LIVE_V3_PLAN_PRICE_IDS[plan.id][currency];
}

export type V3CheckoutResolution =
  | {
      ok: true;
      planId: V3PlanId;
      label: string;
      credits: number;
      amountMinor: number;
      currency: V3Currency;
      priceId: string;
      pricingVersion: 'v3';
      policyId: typeof REHUB_CREDIT_POLICY_V3_ID;
      interval: V3BillingInterval;
      expiryMonths: number;
      rolloverCap: null;
      seats: number;
    }
  | { ok: false; error: 'sales_disabled' | 'unknown_plan' | 'currency_unavailable' | 'price_not_configured' };

/**
 * Server checkout resolver. Rejects client-supplied amounts.
 * A missing Price ID fails that currency only. It never substitutes GBP.
 */
export function resolveV3SubscriptionCheckout(
  planId: string | null | undefined,
  currency: string | null | undefined,
  env: StripePriceEnv = {}
): V3CheckoutResolution {
  if (!isCreditPolicyV3SalesEnabled(env)) return { ok: false, error: 'sales_disabled' };
  const plan = getV3Plan(planId);
  if (!plan) return { ok: false, error: 'unknown_plan' };
  const cur = normalizeV3Currency(currency);
  if (!cur) return { ok: false, error: 'currency_unavailable' };
  const priceId = getV3PlanStripePriceId(plan, env, cur);
  if (!priceId) return { ok: false, error: 'price_not_configured' };
  return {
    ok: true,
    planId: plan.id,
    label: plan.label,
    credits: plan.credits,
    amountMinor: plan.amountMinor[cur],
    currency: cur,
    priceId,
    pricingVersion: 'v3',
    policyId: REHUB_CREDIT_POLICY_V3_ID,
    interval: plan.interval,
    expiryMonths: plan.expiryMonths,
    rolloverCap: null,
    seats: plan.seats,
  };
}

export function v3SubscriptionMetadata(resolved: Extract<V3CheckoutResolution, { ok: true }>): Record<string, string> {
  return {
    purchase_type: 'credit_subscription',
    product_family: 'rehub_credits',
    plan_id: resolved.planId,
    plan_name: resolved.label,
    credits: String(resolved.credits),
    credit_amount: String(resolved.credits),
    currency: resolved.currency,
    amount_minor: String(resolved.amountMinor),
    pricing_version: 'v3',
    policy_id: resolved.policyId,
    billing_interval: resolved.interval,
    expiry_months: String(resolved.expiryMonths),
    rollover_cap: '',
    seats: String(resolved.seats),
    credit_type: 'subscription',
  };
}

/**
 * GBP top-ups reuse the existing pack Price IDs because those amounts match.
 * EUR and USD use their own v3 Price IDs. A missing mapping does not fall back to GBP.
 */
export function resolveV3TopUpPolicy(
  packId: string | null | undefined,
  currency: string | null | undefined,
  env: StripePriceEnv = {}
):
  | {
      ok: true;
      packId: string;
      credits: number;
      amountMinor: number;
      priceId: string;
      pricingVersion: 'v3';
      policyId: typeof REHUB_CREDIT_POLICY_V3_ID;
      expiryMonths: number;
      neverExpires: false;
    }
  | { ok: false; error: 'sales_disabled' | 'unknown_pack' | 'currency_unavailable' | 'price_not_configured' } {
  if (!isCreditPolicyV3SalesEnabled(env)) return { ok: false, error: 'sales_disabled' };
  const topup = REHUB_CREDIT_TOPUPS_V3.find((row) => row.id === packId);
  if (!topup) return { ok: false, error: 'unknown_pack' };
  const cur = normalizeV3Currency(currency);
  if (!cur) return { ok: false, error: 'currency_unavailable' };
  let priceId = '';
  if (cur === 'GBP') {
    const pack = REHUB_CREDIT_PACKS_V2.find((row) => row.id === topup.id);
    if (!pack || pack.amountMinor.GBP !== topup.amountMinor.GBP || pack.credits !== topup.credits) {
      return { ok: false, error: 'unknown_pack' };
    }
    priceId = getPackStripePriceIdV2(pack, 'GBP', env);
  } else {
    const keys = v3TopUpPriceEnvKeys(topup.id, cur);
    const fromEnv = env[keys.vite] || env[keys.server] || '';
    priceId = (fromEnv && !RETIRED_TOPUP_PRICE_IDS.includes(fromEnv) ? fromEnv : '')
      || LIVE_V3_TOPUP_PRICE_IDS[topup.id][cur] || '';
  }
  if (!priceId) return { ok: false, error: 'price_not_configured' };
  return {
    ok: true,
    packId: topup.id,
    credits: topup.credits,
    amountMinor: topup.amountMinor[cur],
    priceId,
    pricingVersion: 'v3',
    policyId: REHUB_CREDIT_POLICY_V3_ID,
    expiryMonths: topup.expiryMonths,
    neverExpires: false,
  };
}

export interface ResolvedV3Grant {
  planId: V3PlanId;
  credits: number;
  label: string;
  pricingVersion: 'v3';
  rolloverCap: null;
  interval: V3BillingInterval;
  expiryMonths: number;
  policyId: typeof REHUB_CREDIT_POLICY_V3_ID;
}

export function buildV3SubscriptionPriceCreditMap(env: StripePriceEnv = {}): Record<string, ResolvedV3Grant> {
  const map: Record<string, ResolvedV3Grant> = {};
  for (const plan of REHUB_CREDIT_PLANS_V3) {
    for (const currency of V3_CURRENCIES) {
      const priceId = getV3PlanStripePriceId(plan, env, currency);
      if (!priceId) continue;
      map[priceId] = {
      planId: plan.id,
      credits: plan.credits,
      label: plan.label,
      pricingVersion: 'v3',
      rolloverCap: null,
      interval: plan.interval,
      expiryMonths: plan.expiryMonths,
      policyId: REHUB_CREDIT_POLICY_V3_ID,
    };
    }
  }
  return map;
}

export function getV3SubscriptionByStripePriceId(
  priceId: string | null | undefined,
  env: StripePriceEnv = {}
): ResolvedV3Grant | null {
  if (!priceId) return null;
  return buildV3SubscriptionPriceCreditMap(env)[priceId] ?? null;
}

/**
 * Proportional credit reversal against the original allocation.
 * Uses the cumulative refunded amount so repeated partial refunds cannot
 * exceed the credits that payment bought.
 */
export function proportionalCreditsToReverse(opts: {
  originalCredits: number;
  originalMinor: number;
  cumulativeRefundedMinor: number;
  alreadyReversedCredits: number;
}): number {
  const originalCredits = opts.originalCredits;
  const originalMinor = opts.originalMinor;
  if (!Number.isFinite(originalCredits) || originalCredits <= 0) return 0;
  if (!Number.isFinite(originalMinor) || originalMinor <= 0) return 0;
  const refunded = Math.max(0, Math.min(opts.cumulativeRefundedMinor, originalMinor));
  const target = Math.round((originalCredits * refunded) / originalMinor);
  const already = Math.max(0, opts.alreadyReversedCredits);
  return Math.max(0, target - already);
}

export function missingV3StripePriceIds(env: StripePriceEnv = {}): string[] {
  const missing: string[] = [];
  for (const plan of REHUB_CREDIT_PLANS_V3) {
    for (const currency of V3_CURRENCIES) {
      if (!getV3PlanStripePriceId(plan, env, currency)) missing.push(`${plan.id}:${currency}`);
    }
  }
  for (const pack of REHUB_CREDIT_TOPUPS_V3) {
    for (const currency of V3_CURRENCIES) {
      if (currency === 'GBP') continue;
      const resolved = resolveV3TopUpPolicy(pack.id, currency, {
        ...env,
        REHUB_PRICING_V3_SALES: 'true',
        VITE_REHUB_PRICING_V3_SALES: 'true',
      });
      if (!resolved.ok) missing.push(`${pack.id}:${currency}`);
    }
  }
  return missing;
}

export const CREDIT_CHECKOUT_INTENT_TTL_MINUTES = 30;

export const CREDIT_SUBSCRIPTION_PRODUCT_FAMILY = 'rehub_credits';
